import { API_URL } from "./siteConfig";

const API_BASE = API_URL;

interface ApiOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
}

class ApiError extends Error {
  status: number;
  details?: unknown;
  data?: any;

  constructor(message: string, status: number, details?: unknown, data?: any) {
    super(message);
    this.status = status;
    this.details = details;
    this.data = data;
  }
}

async function request<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const token = localStorage.getItem("moremail_token");

  const res = await fetch(`${API_BASE}${endpoint}`, {
    method: options.method || "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
    ...(options.body ? { body: JSON.stringify(options.body) } : {}),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new ApiError(data.error || "Request failed", res.status, data.details, data);
  }

  return res.json();
}

// ── Auth ──
export interface User {
  id: string;
  email: string;
  name: string;
  company?: string;
  plan: string;
  is_admin?: number;
  created_at: string;
}

export interface AuthResponse {
  token: string;
  user: User;
}

export const auth = {
  register: (data: { email: string; password: string; name: string; company?: string }) =>
    request<AuthResponse>("/auth/register", { method: "POST", body: data }),

  login: (data: { email: string; password: string }) =>
    request<AuthResponse>("/auth/login", { method: "POST", body: data }),

  me: () => request<{ user: User }>("/auth/me"),

  updateProfile: (data: { name?: string; company?: string }) =>
    request<{ user: User }>("/auth/me", { method: "PATCH", body: data }),

  changePassword: (current_password: string, new_password: string) =>
    request<{ message: string }>("/auth/me/change-password", {
      method: "POST", body: { current_password, new_password },
    }),

  getTrackingDomain: () =>
    request<{ tracking_domain: string | null }>("/auth/me/tracking-domain"),

  setTrackingDomain: (tracking_domain: string | null) =>
    request<{ tracking_domain: string | null }>("/auth/me/tracking-domain", {
      method: "PUT", body: { tracking_domain },
    }),

  getSunsetSettings: () =>
    request<{ sunset_enabled: number; sunset_days: number; sunset_last_run_at: string | null }>("/auth/me/sunset"),

  setSunsetSettings: (sunset_enabled: number, sunset_days: number) =>
    request<{ sunset_enabled: number; sunset_days: number }>("/auth/me/sunset", {
      method: "PUT", body: { sunset_enabled, sunset_days },
    }),

  // Custom SMTP
  getCustomSmtp: () =>
    request<{ configured: boolean; smtp: { host: string; port: number; secure: boolean; user: string; from_name: string; from_address: string } | null }>("/auth/me/custom-smtp"),

  saveCustomSmtp: (data: { host: string; port: number; secure: boolean; user: string; password: string; from_name?: string; from_address: string }) =>
    request<{ message: string; smtp: any }>("/auth/me/custom-smtp", {
      method: "PUT", body: data,
    }),

  testCustomSmtp: () =>
    request<{ success: boolean; message: string }>("/auth/me/custom-smtp/test", { method: "POST" }),

  deleteCustomSmtp: () =>
    request<{ message: string }>("/auth/me/custom-smtp", { method: "DELETE" }),
};

// ── AWS-style paginated response ──
export interface PaginatedResponse<T> {
  nextToken: string | null;
  count: number;
  scannedCount: number;
  items?: T[];  // generic
}

// ── Campaigns ──
export interface Campaign {
  id: string;
  name: string;
  subject?: string;
  preview_text?: string;
  html_content?: string;
  status: "draft" | "scheduled" | "sending" | "sent" | "active" | "paused";
  scheduled_at?: string;
  sent_at?: string;
  created_at: string;
  updated_at: string;
  sent_count?: number;
  open_count?: number;
  click_count?: number;
  bounce_count?: number;
  // Auto-optimization
  auto_optimize?: number;
  auto_optimize_wait_hours?: number;
  auto_optimize_threshold?: number;
  auto_optimize_status?: "pending" | "triggered" | "completed" | "skipped" | null;
  auto_optimize_checked_at?: string | null;
  parent_campaign_id?: string | null;
  // Auto-reply
  auto_reply_enabled?: number;
  auto_reply_instructions?: string | null;
}

export interface CampaignListResponse {
  campaigns: Campaign[];
  nextToken: string | null;
  count: number;
  scannedCount: number;
}

export interface OptimizationSuggestion {
  subject: string;
  rationale: string;
  predictedImprovement: string;
}

export interface OptimizationResult {
  optimizationId: string;
  openRate: number;
  sent: number;
  opens: number;
  suggestions: OptimizationSuggestion[];
}

export interface SpamScoreResult {
  score: number;
  tab: "primary" | "promotions" | "spam";
  issues: Array<{ severity: "high" | "med" | "low"; message: string }>;
  suggestions: string[];
  linkCount: number;
}

export const campaigns = {
  list: (params?: { status?: string; maxResults?: number; nextToken?: string }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.maxResults) qs.set("maxResults", String(params.maxResults));
    if (params?.nextToken) qs.set("nextToken", params.nextToken);
    return request<CampaignListResponse>(`/campaigns?${qs}`);
  },

  /** Fetch all pages by following nextToken */
  listAll: async (params?: { status?: string; maxResults?: number }): Promise<Campaign[]> => {
    const all: Campaign[] = [];
    let nextToken: string | undefined;
    do {
      const res = await campaigns.list({ ...params, nextToken });
      all.push(...res.campaigns);
      nextToken = res.nextToken || undefined;
    } while (nextToken);
    return all;
  },

  get: (id: string) => request<{ campaign: Campaign }>(`/campaigns/${id}`),

  create: (data: Partial<Campaign>) =>
    request<{ campaign: Campaign }>("/campaigns", { method: "POST", body: data }),

  update: (id: string, data: Partial<Campaign>) =>
    request<{ campaign: Campaign }>(`/campaigns/${id}`, { method: "PUT", body: data }),

  delete: (id: string) => request<{ deleted: boolean }>(`/campaigns/${id}`, { method: "DELETE" }),

  send: (id: string) =>
    request<{ accepted: boolean; status: string; recipients: number; message: string }>(`/campaigns/${id}/send`, { method: "POST" }),

  pause: (id: string) =>
    request<{ campaign: Campaign; status: string }>(`/campaigns/${id}/pause`, { method: "POST" }),

  resume: (id: string) =>
    request<{ campaign: Campaign; status: string }>(`/campaigns/${id}/resume`, { method: "POST" }),

  resendNew: (id: string) =>
    request<{ message: string }>(`/campaigns/${id}/resend-new`, { method: "POST" }),

  previewScore: (id: string) =>
    request<SpamScoreResult>(`/campaigns/${id}/preview-score`, { method: "POST" }),

  optimizeSubjects: (id: string) =>
    request<{ message: string; nonOpenerCount?: number }>(`/campaigns/${id}/optimize-subjects`, { method: "POST" }),

  previewPersonalized: (id: string, subscriberId?: string) =>
    request<{ subscriber: { id: string; email: string; name: string | null }; subject: string; html: string; preview_text: string }>(
      `/campaigns/${id}/preview-personalized`,
      { method: "POST", body: subscriberId ? { subscriber_id: subscriberId } : {} }
    ),

  optimize: (id: string) =>
    request<OptimizationResult>(`/campaigns/${id}/optimize`, { method: "POST" }),

  followup: (id: string, subject: string, optimizationId?: string) =>
    request<{ campaign: Campaign; nonOpenerCount: number }>(
      `/campaigns/${id}/followup`,
      { method: "POST", body: { subject, optimizationId } }
    ),

  autoOptimizeLog: (id: string) =>
    request<{ logs: AutoOptimizeLogEntry[] }>(`/campaigns/${id}/auto-optimize-log`),

  autoReplyLog: (id: string) =>
    request<{ count: number; log: AutoReplyLogEntry[] }>(`/campaigns/${id}/auto-reply-log`),

  sendLog: (id: string, params?: { page?: number; limit?: number; status?: string; search?: string }) => {
    const qs = new URLSearchParams();
    if (params?.page) qs.set("page", String(params.page));
    if (params?.limit) qs.set("limit", String(params.limit));
    if (params?.status) qs.set("status", params.status);
    if (params?.search) qs.set("search", params.search);
    return request<CampaignSendLogResponse>(`/campaigns/${id}/send-log?${qs}`);
  },
};

// ── Campaign Send Log ──
export interface SendLogEvent {
  type: string;
  timestamp: string;
  metadata?: any;
}

export interface SendLogItem {
  subscriber_id: string;
  email: string;
  name: string | null;
  status: string;
  subject: string;
  message_id: string | null;
  html_content: string | null;
  variant_index: number | null;
  sent_at: string | null;
  opened: boolean;
  opened_at: string | null;
  clicked: boolean;
  clicked_at: string | null;
  click_url: string | null;
  bounced: boolean;
  bounced_at: string | null;
  bounce_type: string | null;
  unsubscribed: boolean;
  unsubscribed_at: string | null;
  complained: boolean;
  error: string | null;
  events: SendLogEvent[];
  queued_at?: string;
}

export interface SendLogSummary {
  sent: number;
  failed: number;
  opened: number;
  clicked: number;
  bounced: number;
  unsubscribed: number;
  complained: number;
  pending: number;
}

export interface CampaignSendLogResponse {
  campaign: Campaign;
  items: SendLogItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  summary?: SendLogSummary;
}

// ── Auto-Optimization Log ──
export interface AutoOptimizeLogEntry {
  id: string;
  campaign_id: string;
  user_id: string;
  action: "checked" | "triggered" | "followup_created" | "followup_sent" | "skipped";
  open_rate: number | null;
  threshold: number | null;
  non_opener_count: number | null;
  new_subject: string | null;
  followup_campaign_id: string | null;
  details: any;
  created_at: string;
}

export interface AutoReplyLogEntry {
  id: string;
  campaign_id: string;
  subscriber_id: string;
  subscriber_email?: string;
  subscriber_name?: string;
  inbound_mail_id: string | null;
  inbound_text: string | null;
  intent: string | null;
  vertical: string | null;
  classification: string | null;
  generated_reply: string | null;
  status: "sent" | "failed" | "skipped";
  error: string | null;
  message_id: string | null;
  created_at: string;
}

// ── Subscribers ──
// ── Email validation ──
export type EmailStatus = "unknown" | "valid" | "invalid" | "risky";

export interface ValidationResult {
  email:   string;
  status:  EmailStatus;
  reason:  string;
  source:  "mx" | "zerobounce" | "syntax";
}

export type EngagementTier = "hot" | "warm" | "cold" | "new";

export interface Subscriber {
  id: string;
  email: string;
  name?: string;
  status: string;
  tags: string[];
  metadata: Record<string, unknown>;
  subscribed_at: string;
  created_at: string;
  email_status?: EmailStatus;
  email_status_checked_at?: string | null;
  engagement_score?: number;
  engagement_tier?: EngagementTier;
  engagement_scored_at?: string | null;
  last_engaged_at?: string | null;
  optimal_send_hour?: number | null;
}

export interface SubscriberListResponse {
  subscribers: Subscriber[];
  nextToken: string | null;
  count: number;
  scannedCount: number;
}

export interface SubscriberStats {
  total: string;
  active: string;
  unsubscribed: string;
  bounced: string;
  new_last_30d: string;
}

export type SentEmailListItem = {
  id: string;
  campaign_id: string | null;
  campaign_name: string | null;
  to_email: string;
  subject: string;
  message_id: string | null;
  sent_at: string;
};
export type SentEmail = SentEmailListItem & { html_content: string };

export const subscribers = {
  list: (params?: { status?: string; tag?: string; search?: string; engagement_tier?: EngagementTier; maxResults?: number; nextToken?: string }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.tag) qs.set("tag", params.tag);
    if (params?.search) qs.set("search", params.search);
    if (params?.engagement_tier) qs.set("engagement_tier", params.engagement_tier);
    if (params?.maxResults) qs.set("maxResults", String(params.maxResults));
    if (params?.nextToken) qs.set("nextToken", params.nextToken);
    return request<SubscriberListResponse>(`/subscribers?${qs}`);
  },

  /** Fetch all pages */
  listAll: async (params?: { status?: string; search?: string; engagement_tier?: EngagementTier; maxResults?: number }): Promise<Subscriber[]> => {
    const all: Subscriber[] = [];
    let nextToken: string | undefined;
    do {
      const res = await subscribers.list({ ...params, nextToken });
      all.push(...res.subscribers);
      nextToken = res.nextToken || undefined;
    } while (nextToken);
    return all;
  },

  stats: () => request<{ stats: SubscriberStats }>("/subscribers/stats"),

  engagementStats: () =>
    request<{
      tiers: Array<{ tier: EngagementTier; count: number; avgScore: number }>;
      topEngaged: Subscriber[];
      atRisk: Subscriber[];
    }>("/subscribers/engagement-stats"),

  create: (data: { email: string; name?: string; tags?: string[] }) =>
    request<{ subscriber: Subscriber }>("/subscribers", { method: "POST", body: data }),

  import: (data: { subscribers: Array<{ email: string; name?: string; tags?: string[] }> }) =>
    request<{ imported: number; skipped: number; total: number }>("/subscribers/import", {
      method: "POST",
      body: data,
    }),

  update: (id: string, data: Partial<Subscriber>) =>
    request<{ subscriber: Subscriber }>(`/subscribers/${id}`, { method: "PUT", body: data }),

  delete: (id: string) => request<{ deleted: boolean }>(`/subscribers/${id}`, { method: "DELETE" }),

  enrich: (id: string, force = false) =>
    request<{ enrichment: EnrichmentData }>(`/subscribers/${id}/enrich`, { method: "POST", body: { force } }),

  /** @deprecated use enrich() — the GET /enrichment route does not exist on the backend */
  getEnrichment: (id: string) =>
    request<{ enrichment: EnrichmentData }>(`/subscribers/${id}/enrich`, { method: "POST", body: { force: false } }),

  bulkEnrich: () =>
    request<{ accepted: boolean; total: number; message: string }>("/subscribers/bulk-enrich", { method: "POST" }),

  validate: (id: string) =>
    request<{ result: ValidationResult }>(`/subscribers/${id}/validate`, { method: "POST" }),

  bulkValidate: (force = false) =>
    request<{ accepted: boolean; total: number; message: string }>(
      `/subscribers/bulk-validate${force ? "?force=true" : ""}`,
      { method: "POST" }
    ),

  validateEmail: (email: string) =>
    request<{ result: ValidationResult }>("/subscribers/validate-email", {
      method: "POST", body: { email },
    }),

  listEmails: (id: string) =>
    request<{ emails: SentEmailListItem[]; count: number }>(`/subscribers/${id}/emails`),

  getEmail: (subscriberId: string, emailId: string) =>
    request<SentEmail>(`/subscribers/${subscriberId}/emails/${emailId}`),
};

// ── Analytics ──
export interface DashboardAnalytics {
  subscribers: {
    total_subscribers: string;
    active_subscribers: string;
    new_subscribers_30d: string;
  };
  campaigns: {
    total_campaigns: string;
    sent_campaigns: string;
    draft_campaigns: string;
    active_campaigns: string;
    scheduled_campaigns: string;
  };
  performance: {
    total_sent: number;
    total_opens: number;
    total_clicks: number;
    total_bounces: number;
    open_rate: string;
    click_rate: string;
  };
}

export const analytics = {
  dashboard: () => request<DashboardAnalytics>("/analytics/dashboard"),

  timeseries: (days?: number) => {
    const qs = days ? `?days=${days}` : "";
    return request<{ timeseries: Array<{ date: string; sent: string; opens: string; clicks: string }> }>(
      `/analytics/timeseries${qs}`
    );
  },

  campaign: (id: string) =>
    request<{ campaign: { id: string; name: string }; events: Array<{ event_type: string; count: string }> }>(
      `/analytics/campaigns/${id}`
    ),

  engagementDrilldown: (params: { type: string; campaign_id?: string; page?: number; limit?: number; search?: string }) => {
    const qs = new URLSearchParams();
    qs.set("type", params.type);
    if (params.campaign_id) qs.set("campaign_id", params.campaign_id);
    if (params.page) qs.set("page", String(params.page));
    if (params.limit) qs.set("limit", String(params.limit));
    if (params.search) qs.set("search", params.search);
    return request<{
      subscribers: Array<{
        id: string; email: string; name: string | null; status: string;
        engagement_tier: string; first_event?: string; last_event?: string;
        event_count?: number; campaign_name?: string; campaign_id?: string;
        created_at?: string;
      }>;
      total: number; page: number; limit: number;
      campaigns?: Array<{ id: string; name: string }>;
    }>(`/analytics/engagement-drilldown?${qs.toString()}`);
  },
};

// ── Templates ──
export interface EmailBlock {
  type: "heading" | "text" | "image" | "button" | "divider" | "spacer" | "columns";
  id: string;
  [key: string]: any;
}

export interface Template {
  id: string;
  name: string;
  description?: string;
  blocks: EmailBlock[];
  is_preset: number;
  created_at: string;
  updated_at: string;
}

export const templates = {
  list: () => request<{ templates: Template[] }>("/templates"),

  get: (id: string) => request<{ template: Template }>(`/templates/${id}`),

  create: (data: { name: string; description?: string; blocks: EmailBlock[] }) =>
    request<{ template: Template }>("/templates", { method: "POST", body: data }),

  update: (id: string, data: { name?: string; description?: string; blocks?: EmailBlock[] }) =>
    request<{ template: Template }>(`/templates/${id}`, { method: "PUT", body: data }),

  delete: (id: string) => request<{ deleted: boolean }>(`/templates/${id}`, { method: "DELETE" }),

  render: (blocks: EmailBlock[], preheader?: string) =>
    request<{ html: string }>("/templates/render", { method: "POST", body: { blocks, preheader } }),

  renderById: (id: string, preheader?: string) =>
    request<{ html: string }>(`/templates/${id}/render`, { method: "POST", body: { preheader } }),
};

// ── AI Copywriter ──
export type AiTone = "professional" | "casual" | "urgent" | "playful" | "friendly";

export type AiGoal = "announce" | "promote" | "nurture" | "re-engage";

export interface CampaignFullRequest {
  productUrl: string;
  audience: string;
  goal: AiGoal;
  tone?: AiTone;
  cta?: string;
  ctaUrl?: string;
  brandName?: string;
}

export interface CampaignFullResult {
  subjectLine: string;
  preheader: string;
  html: string;
  alternativeSubjects: Array<{ text: string; whyItWorks: string }>;
}

export const ai = {
  subjectLines: (data: { brief: string; tone?: AiTone; count?: number; brandName?: string }) =>
    request<{ subjects: Array<{ text: string; whyItWorks: string }> }>("/ai/subject-lines", { method: "POST", body: data }),

  emailBody: (data: { brief: string; tone?: AiTone; subjectLine?: string; brandName?: string; callToAction?: string }) =>
    request<{ preheader: string; heading: string; body: string; callToAction: string }>("/ai/email-body", { method: "POST", body: data }),

  rewrite: (data: { content: string; instruction: string; tone?: AiTone }) =>
    request<{ rewritten: string }>("/ai/rewrite", { method: "POST", body: data }),

  campaignFull: (data: CampaignFullRequest) =>
    request<CampaignFullResult>("/ai/campaign-full", { method: "POST", body: data }),
};

// ── AI Multi-Step Campaign Sequences ──
export type SequenceIndustry = "general" | "ecommerce" | "saas" | "real_estate" | "local_services" | "agency" | "education" | "health";

export interface SequenceGenerateRequest {
  prompt: string;
  industry?: SequenceIndustry;
  tone?: AiTone;
  brandName?: string;
  ctaUrl?: string;
}

export interface SequenceStep {
  id: string;
  campaignId: string;
  stepNumber: number;
  day: number;
  emailType: string;
  subject: string;
  previewText: string;
  goal: string;
  sendTime: string;
  callToAction: string;
}

export interface SequenceSummary {
  id: string;
  name: string;
  description: string;
  durationDays: number;
  industry: string;
  prompt: string;
  status: string;
  stepCount: number;
}

export interface SequenceGenerateResult {
  sequence: SequenceSummary;
  steps: SequenceStep[];
}

export interface SequenceDetail {
  sequence: any;
  steps: any[];
}

export const sequences = {
  generate: (data: SequenceGenerateRequest) =>
    request<SequenceGenerateResult>("/sequences/generate", { method: "POST", body: data }),

  list: (params?: { status?: string; maxResults?: number; nextToken?: string }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.maxResults) qs.set("maxResults", String(params.maxResults));
    if (params?.nextToken) qs.set("nextToken", params.nextToken);
    return request<{ sequences: any[]; nextToken: string | null; count: number }>(`/sequences?${qs}`);
  },

  get: (id: string) => request<SequenceDetail>(`/sequences/${id}`),

  delete: (id: string) => request<{ deleted: boolean }>(`/sequences/${id}`, { method: "DELETE" }),
};

// ── Prebuilt Campaign Library ──
export interface PrebuiltCampaign {
  id: string;
  slug: string;
  name: string;
  description: string;
  industry: string;
  category: string;
  tags: string[];
  duration_days: number;
  step_count: number;
  difficulty: "beginner" | "intermediate" | "advanced";
  preview_image: string | null;
  sort_order: number;
  install_count: number;
  created_at: string;
}

export interface PrebuiltStep {
  id: string;
  step_number: number;
  day: number;
  email_type: string;
  goal: string;
  send_time: string;
  subject: string;
  preview_text: string;
  body_heading: string;
  body_text: string;
  call_to_action: string;
}

export const prebuilt = {
  list: (params?: { industry?: string; category?: string; search?: string }) => {
    const qs = new URLSearchParams();
    if (params?.industry) qs.set("industry", params.industry);
    if (params?.category) qs.set("category", params.category);
    if (params?.search) qs.set("search", params.search);
    return request<{ campaigns: PrebuiltCampaign[]; count: number }>(`/prebuilt?${qs}`);
  },
  industries: () => request<{ industries: { industry: string; campaign_count: number }[]; categories: { category: string; campaign_count: number }[] }>("/prebuilt/industries"),
  get: (idOrSlug: string) => request<{ campaign: PrebuiltCampaign; steps: PrebuiltStep[] }>(`/prebuilt/${idOrSlug}`),
  install: (idOrSlug: string, data?: { brandName?: string; ctaUrl?: string }) =>
    request<{ sequence: any; steps: any[]; source: any }>(`/prebuilt/${idOrSlug}/install`, { method: "POST", body: data }),
  seed: () => request<{ message: string; total: number; created: number; updated: number }>("/prebuilt/admin/seed", { method: "POST" }),
};

// ── Prebuilt Automation Library ──
export interface PrebuiltAutomation {
  id: string;
  slug: string;
  name: string;
  description: string;
  industry: string;
  category: string;
  tags: string[];
  trigger_type: string;
  step_count: number;
  difficulty: "beginner" | "intermediate" | "advanced";
  sort_order: number;
  install_count: number;
  flow_data?: { steps: FlowStep[] };
  created_at: string;
}

export const prebuiltAutomations = {
  list: (params?: { industry?: string; category?: string; search?: string }) => {
    const qs = new URLSearchParams();
    if (params?.industry) qs.set("industry", params.industry);
    if (params?.category) qs.set("category", params.category);
    if (params?.search) qs.set("search", params.search);
    return request<{ automations: PrebuiltAutomation[]; count: number }>(`/prebuilt-automations?${qs}`);
  },
  get: (idOrSlug: string) => request<{ automation: PrebuiltAutomation }>(`/prebuilt-automations/${idOrSlug}`),
  install: (idOrSlug: string) =>
    request<{ automation: any; source: any }>(`/prebuilt-automations/${idOrSlug}/install`, { method: "POST" }),
  seed: () => request<{ message: string; total: number; created: number; updated: number }>("/prebuilt-automations/admin/seed", { method: "POST" }),
};

// ── Smart Recommendations ──
export interface Recommendation {
  id: string;
  priority: "high" | "medium" | "low";
  category: "deliverability" | "engagement" | "growth" | "optimization" | "setup" | "revenue";
  title: string;
  description: string;
  actionLabel: string;
  actionLink: string;
  metric?: string;
  metricLabel?: string;
}

export interface RecommendationsResponse {
  recommendations: Recommendation[];
  summary: { total: number; high: number; medium: number; low: number };
  snapshot: { subscribers: number; campaigns: number; automations: number; openRate: number | null; bounceRate: number | null };
}

export const recommendations = {
  get: () => request<RecommendationsResponse>("/recommendations"),
};

// ── A/B Test Archive ──
export interface AbTestVariant {
  index: number;
  subject: string;
  label: string;
  sent: number;
  opens: number;
  clicks: number;
  bounces: number;
  openRate: number;
  clickRate: number;
  bounceRate: number;
}

export interface AbTestResult {
  campaignId: string;
  campaignName: string;
  sentAt: string;
  variantCount: number;
  variants: AbTestVariant[];
  winner: { label: string; subject: string; openRate: number; clickRate: number; uplift: number };
  totalSent: number;
  totalOpens: number;
}

export interface AbTestInsights {
  totalTests: number;
  avgWinnerOpenRate: number;
  avgUplift: number;
  patterns: string[];
}

export const abTests = {
  list: () => request<{ tests: AbTestResult[]; insights: AbTestInsights | null }>("/ab-tests"),
};

// ── Competitor Tracker ──
export interface CompetitorTrack {
  id: string;
  competitor_name: string;
  competitor_domain: string;
  monitoring_email: string | null;
  status: string;
  email_count: number;
  last_email_at: string | null;
  created_at: string;
}

export interface CompetitorEmail {
  id: string;
  from_address: string;
  subject: string | null;
  preview_text: string | null;
  word_count: number;
  has_images: number;
  link_count: number;
  cta_text: string | null;
  received_at: string;
  body_text?: string;
  body_html?: string;
}

export interface CompetitorAnalysis {
  competitors: Array<CompetitorTrack & { avg_word_count: number; avg_links: number; emails_last_30d: number }>;
  patterns: { byDayOfWeek: { day: string; count: number }[]; byHour: { hour: number; count: number }[] };
  subjectLineStats: { avgLength: number; minLength: number; maxLength: number };
  topCtas: { text: string; count: number }[];
}

export const competitors = {
  list: () => request<{ competitors: CompetitorTrack[] }>("/competitors"),
  add: (data: { competitorName: string; competitorDomain: string; monitoringEmail?: string }) =>
    request<{ competitor: CompetitorTrack }>("/competitors", { method: "POST", body: data }),
  remove: (id: string) => request<{ removed: boolean }>(`/competitors/${id}`, { method: "DELETE" }),
  emails: (id: string) => request<{ emails: CompetitorEmail[] }>(`/competitors/${id}/emails`),
  email: (trackId: string, emailId: string) => request<{ email: CompetitorEmail }>(`/competitors/${trackId}/emails/${emailId}`),
  analysis: () => request<CompetitorAnalysis>("/competitors/analysis/overview"),
};

// ── Landing Pages ──
export interface LandingPage {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  template: string;
  blocks: any[];
  form_config: { fields?: string[]; buttonText?: string; successMessage?: string; tags?: string[] };
  style_config: { bgColor?: string; primaryColor?: string; textColor?: string };
  meta_title: string | null;
  meta_description: string | null;
  status: "draft" | "published" | "archived";
  published_at: string | null;
  view_count: number;
  submission_count: number;
  created_at: string;
  updated_at: string;
}

export interface LandingPageSubmission {
  id: string;
  email: string;
  name: string | null;
  fields: Record<string, any>;
  subscriber_id: string | null;
  created_at: string;
}

export const landingPages = {
  templates: () => request<{ templates: { id: string; name: string; description: string }[] }>("/landing-pages/templates"),
  list: () => request<{ pages: LandingPage[] }>("/landing-pages"),
  get: (id: string) => request<{ page: LandingPage }>(`/landing-pages/${id}`),
  create: (data: { title: string; slug: string; template?: string }) =>
    request<{ page: LandingPage }>("/landing-pages", { method: "POST", body: data }),
  update: (id: string, data: Partial<{ title: string; blocks: any[]; form_config: any; style_config: any; meta_title: string; meta_description: string }>) =>
    request<{ page: LandingPage }>(`/landing-pages/${id}`, { method: "PUT", body: data }),
  publish: (id: string) => request<{ status: string }>(`/landing-pages/${id}/publish`, { method: "POST" }),
  unpublish: (id: string) => request<{ status: string }>(`/landing-pages/${id}/unpublish`, { method: "POST" }),
  delete: (id: string) => request<{ deleted: boolean }>(`/landing-pages/${id}`, { method: "DELETE" }),
  submissions: (id: string) => request<{ submissions: LandingPageSubmission[] }>(`/landing-pages/${id}/submissions`),
};

// ── Enrichment ──
export interface EnrichmentData {
  company: string;
  title: string;
  linkedinUrl: string;
  techStack: string[];
  funding: string;
  companyNews: string;
  recentPost: string;
}

export const PERSONALIZATION_TOKENS = [
  { token: "{{first_name}}",   label: "First Name",    description: "Subscriber's first name" },
  { token: "{{full_name}}",    label: "Full Name",     description: "Subscriber's full name" },
  { token: "{{email}}",        label: "Email",         description: "Subscriber's email address" },
  { token: "{{company}}",      label: "Company",       description: "Their company name (enriched)" },
  { token: "{{title}}",        label: "Job Title",     description: "Their job title (enriched)" },
  { token: "{{tech_stack}}",   label: "Tech Stack",    description: "Technologies they use (enriched)" },
  { token: "{{funding}}",      label: "Funding",       description: "Recent funding info (enriched)" },
  { token: "{{company_news}}", label: "Company News",  description: "Recent company news (enriched)" },
  { token: "{{recent_post}}",  label: "Recent Post",   description: "Their recent social post (enriched)" },
] as const;

// ── Intent Lead Discovery ──
export interface IntentKeyword {
  id: string;
  user_id: string;
  keyword: string;
  platforms: string[];
  is_active: number;
  created_at: string;
}

export interface IntentLead {
  id: string;
  user_id: string;
  keyword_id: string | null;
  source: string;
  source_url: string | null;
  post_text: string | null;
  author_handle: string | null;
  contact_email: string | null;
  company: string | null;
  intent_score: number;
  status: "new" | "approved" | "rejected" | "sent";
  campaign_id: string | null;
  subscriber_id: string | null;
  created_at: string;
}

export interface IntentLeadListResponse {
  leads: IntentLead[];
  nextToken: string | null;
  count: number;
  scannedCount: number;
}

export const intent = {
  // Keywords
  listKeywords: () =>
    request<{ keywords: IntentKeyword[] }>("/intent/keywords"),
  createKeyword: (keyword: string, platforms?: string[]) =>
    request<{ keyword: IntentKeyword }>("/intent/keywords", {
      method: "POST", body: { keyword, platforms: platforms ?? ["reddit"] },
    }),
  deleteKeyword: (id: string) =>
    request<{ deactivated: boolean }>(`/intent/keywords/${id}`, { method: "DELETE" }),

  // Leads
  listLeads: (params?: { status?: string; maxResults?: number; nextToken?: string }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.maxResults) qs.set("maxResults", String(params.maxResults));
    if (params?.nextToken) qs.set("nextToken", params.nextToken);
    return request<IntentLeadListResponse>(`/intent/leads?${qs}`);
  },
  getLead: (id: string) =>
    request<{ lead: IntentLead }>(`/intent/leads/${id}`),
  approveLead: (id: string) =>
    request<{ approved: boolean; subscriber: any }>(`/intent/leads/${id}/approve`, { method: "POST" }),
  rejectLead: (id: string) =>
    request<{ rejected: boolean }>(`/intent/leads/${id}/reject`, { method: "POST" }),
  scanNow: () =>
    request<{ accepted: boolean; message: string }>("/intent/scan-now", { method: "POST" }),
};

// ── Deliverability ──
export interface BlacklistResult {
  list:      string;
  domain:    string;
  weight:    "critical" | "high" | "medium";
  listed:    boolean;
  reason?:   string;
  delistUrl?: string;
}

export interface BlacklistCheckSummary {
  ip:        string;
  checkedAt: string;
  anyListed: boolean;
  listedOn:  string[];
  results:   BlacklistResult[];
}

export interface DeliverabilityTotals {
  sent: number;
  opens: number;
  bounces: number;
  complaints: number;
  openRate: number;
  bounceRate: number;
  complaintRate: number;
  reputationScore: number | null;  // null when no sends yet
}

export interface DeliverabilityTrend {
  week: string;
  sent: number;
  opens: number;
  bounces: number;
  complaints: number;
  openRate: number;
  bounceRate: number;
  complaintRate: number;
}

export interface WarmupPlan {
  id: string;
  user_id: string;
  domain: string;
  day_number: number;
  daily_limit: number;
  emails_sent_today: number;
  last_reset_date: string | null;
  is_active: number;
  created_at: string;
}

export interface DeliverabilityResponse {
  totals:    DeliverabilityTotals;
  trend:     DeliverabilityTrend[];
  warmup:    WarmupPlan | null;
  blacklist: BlacklistCheckSummary | null;
}

export interface WarmupInbox {
  id: string;
  email: string;
  label: string | null;
  provider: "gmail" | "outlook" | "yahoo" | "icloud" | "custom";
  is_active: boolean;
  sent_today: number;
  target_today: number;
  sent_total: number;
  marked_safe_total: number;
  last_test_status: string | null;
  last_test_error: string | null;
  last_tested_at: string | null;
  consecutive_send_failures: number;
  last_send_error: string | null;
  joined_at: string;
}

export interface WarmupPoolStats {
  poolSize:        number;
  sentToday:       number;
  sentTotal:       number;
  markedSafe:      number;
  dailyTarget:     number;
  isActive:        boolean;
  hasCampaignSmtp: boolean;
  inboxes:         WarmupInbox[];
}

export interface WarmupInboxInput {
  email: string;
  label?: string | null;
  provider: "gmail" | "outlook" | "yahoo" | "icloud" | "custom";
  imap_host?: string | null;
  imap_port?: number | null;
  imap_tls?: boolean;
  imap_user?: string | null;
  imap_password?: string;
}

export const deliverability = {
  stats: () => request<DeliverabilityResponse>("/analytics/deliverability"),
  listWarmup: () => request<{ warmup: WarmupPlan[] }>("/analytics/warmup"),
  createWarmup: (domain: string) =>
    request<{ warmup: WarmupPlan }>("/analytics/warmup", { method: "POST", body: { domain } }),
  deleteWarmup: (id: string) =>
    request<{ deactivated: boolean }>(`/analytics/warmup/${id}`, { method: "DELETE" }),
  resetWarmup: (id: string) =>
    request<{ reset: boolean; warmup: WarmupPlan }>(`/analytics/warmup/${id}/reset`, { method: "POST" }),
  checkBlacklist: () =>
    request<{ blacklist: BlacklistCheckSummary }>(
      "/analytics/deliverability/blacklist", { method: "POST" }
    ),
  getWarmupPool: () =>
    request<{ stats: WarmupPoolStats }>("/analytics/warmup-pool"),
  joinWarmupPool: () =>
    request<{ joined: boolean }>("/analytics/warmup-pool/join", { method: "POST" }),
  leaveWarmupPool: () =>
    request<{ left: boolean }>("/analytics/warmup-pool/leave", { method: "POST" }),
  resetWarmupPool: () =>
    request<{ reset: boolean; stats: WarmupPoolStats }>("/analytics/warmup-pool/reset", { method: "POST" }),

  // Multi-inbox CRUD (receivers only — they never send)
  listInboxes: () =>
    request<{ inboxes: WarmupInbox[] }>("/analytics/warmup-pool/inboxes"),
  addInbox: (body: WarmupInboxInput) =>
    request<{ added: boolean; id: string; stats: WarmupPoolStats }>(
      "/analytics/warmup-pool/inboxes", { method: "POST", body }
    ),
  updateInbox: (id: string, body: { is_active?: boolean; label?: string | null }) =>
    request<{ updated: boolean; stats: WarmupPoolStats }>(
      `/analytics/warmup-pool/inboxes/${id}`, { method: "PATCH", body }
    ),
  testInbox: (id: string) =>
    request<{ ok: boolean; status: string; error?: string }>(
      `/analytics/warmup-pool/inboxes/${id}/test`, { method: "POST" }
    ),
  deleteInbox: (id: string) =>
    request<{ deleted: boolean }>(`/analytics/warmup-pool/inboxes/${id}`, { method: "DELETE" }),
  testSend: () =>
    request<{ sent: number; failed: number; results: { inbox: string; messageId: string | null; error?: string }[] }>(
      "/analytics/warmup-pool/test-send", { method: "POST" }
    ),
};

// ── Integrations ──
export interface Integration {
  id: string;
  platform: "linkedin" | "twilio" | "vonage";
  status: "active" | "error" | "expired";
  created_at: string;
  updated_at: string;
}

export const integrations = {
  list: () => request<{ integrations: Integration[] }>("/integrations"),
  connectTwilio: (data: { accountSid: string; authToken: string; fromNumber: string }) =>
    request<{ integration: Integration }>("/integrations", {
      method: "POST", body: { platform: "twilio", ...data },
    }),
  connectVonage: (data: { apiKey: string; apiSecret: string; fromNumber: string }) =>
    request<{ integration: Integration }>("/integrations", {
      method: "POST", body: { platform: "vonage", ...data },
    }),
  getLinkedInOAuthUrl: () =>
    request<{ url: string }>("/integrations/linkedin/oauth/url"),
  disconnect: (platform: string) =>
    request<{ deleted: boolean }>(`/integrations/${platform}`, { method: "DELETE" }),
};

// ── Automations ──
export type AutomationTriggerType =
  | "subscriber_added" | "tag_added" | "manual" | "reply_received"
  | "email_opened" | "link_clicked" | "campaign_sent" | "inactivity" | "webhook_received";

export interface FlowStep {
  type: "trigger" | "delay" | "condition" | "send" | "webhook" | "linkedin_message" | "sms_message";
  [key: string]: any;
}

export interface Automation {
  id: string;
  name: string;
  trigger_type: AutomationTriggerType;
  flow_data: { steps: FlowStep[] };
  status: "draft" | "active" | "paused";
  active_runs?: number;
  completed_runs?: number;
  created_at: string;
  updated_at: string;
}

export interface AutomationRun {
  id: string;
  automation_id: string;
  subscriber_id: string;
  subscriber_email: string;
  subscriber_name?: string;
  current_step: number;
  status: string;
  completed_steps: number[];
  started_at: string;
  completed_at?: string;
}

export const automations = {
  list: () => request<{ automations: Automation[] }>("/automations"),

  get: (id: string) => request<{ automation: Automation; runStats: Array<{ status: string; count: number }> }>(`/automations/${id}`),

  create: (data: { name: string; trigger_type: AutomationTriggerType; flow_data: { steps: FlowStep[] } }) =>
    request<{ automation: Automation }>("/automations", { method: "POST", body: data }),

  update: (id: string, data: Partial<{ name: string; trigger_type: AutomationTriggerType; flow_data: { steps: FlowStep[] } }>) =>
    request<{ automation: Automation }>(`/automations/${id}`, { method: "PUT", body: data }),

  activate: (id: string) => request<{ status: string }>(`/automations/${id}/activate`, { method: "POST" }),

  pause: (id: string) => request<{ status: string }>(`/automations/${id}/pause`, { method: "POST" }),

  trigger: (id: string, subscriberId: string) =>
    request<{ triggered: boolean }>(`/automations/${id}/trigger`, { method: "POST", body: { subscriberId } }),

  triggerWebhook: (event: string, target: { subscriberId?: string; email?: string }, data?: Record<string, any>) =>
    request<{ triggered: boolean; event: string; subscriberId: string }>(
      "/automations/trigger/webhook",
      { method: "POST", body: { event, ...target, data } }
    ),

  runs: (id: string) => request<{ runs: AutomationRun[] }>(`/automations/${id}/runs`),

  delete: (id: string) => request<{ deleted: boolean }>(`/automations/${id}`, { method: "DELETE" }),
};

// ── Conversions / Revenue Attribution ──
export interface Conversion {
  id: string;
  user_id: string;
  subscriber_id: string | null;
  campaign_id: string | null;
  event: string;
  revenue: number | null;
  currency: string;
  source: string;
  external_id: string | null;
  metadata: any;
  subscriber_email?: string;
  subscriber_name?: string;
  campaign_name?: string;
  converted_at: string;
}

export interface ConversionSummary {
  totals: {
    total_conversions: number;
    total_revenue: number;
    unique_converters: number;
    campaigns_with_conversions: number;
  };
  byCampaign: Array<{ campaign_id: string; campaign_name: string; conversions: number; revenue: number }>;
  byEvent: Array<{ event: string; conversions: number; revenue: number }>;
  daily: Array<{ date: string; conversions: number; revenue: number }>;
}

export const conversions = {
  record: (data: {
    event: string;
    revenue?: number;
    currency?: string;
    source?: string;
    externalId?: string;
    subscriberId?: string;
    email?: string;
    campaignId?: string;
    metadata?: Record<string, any>;
  }) => request<{ conversion: Conversion }>("/conversions", { method: "POST", body: data }),

  list: (params?: { campaignId?: string; event?: string; days?: number; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.campaignId) qs.set("campaignId", params.campaignId);
    if (params?.event) qs.set("event", params.event);
    if (params?.days) qs.set("days", String(params.days));
    if (params?.limit) qs.set("limit", String(params.limit));
    return request<{ conversions: Conversion[] }>(`/conversions?${qs}`);
  },

  summary: (days?: number) => {
    const qs = days ? `?days=${days}` : "";
    return request<ConversionSummary>(`/conversions/summary${qs}`);
  },

  campaign: (id: string) =>
    request<{ stats: any; conversions: Conversion[] }>(`/conversions/campaign/${id}`),
};

// ── Admin ──
export interface AdminUser {
  id: string;
  email: string;
  name: string;
  company?: string;
  plan: string;
  is_admin: number;
  is_disabled: number;
  subscriber_limit: number;
  monthly_email_limit: number;
  emails_sent_this_month: number;
  subscriber_count?: number;
  campaign_count?: number;
  active_subscribers?: number;
  created_at: string;
  updated_at: string;
}

export interface AuditLogEntry {
  id: string;
  admin_id: string;
  admin_email: string;
  admin_name: string;
  action: string;
  target_type: string;
  target_id?: string;
  details: Record<string, any>;
  ip_address?: string;
  created_at: string;
}

export const admin = {
  users: (params?: { search?: string; plan?: string; disabled?: string; page?: number; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.search) qs.set("search", params.search);
    if (params?.plan) qs.set("plan", params.plan);
    if (params?.disabled) qs.set("disabled", params.disabled);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.limit) qs.set("limit", String(params.limit));
    return request<{ users: AdminUser[]; total: number }>(`/admin/users?${qs}`);
  },

  getUser: (id: string) =>
    request<{ user: AdminUser; subscriberStats: any; campaignStats: any; emailStats: any }>(`/admin/users/${id}`),

  updateUser: (id: string, data: Partial<{ plan: string; is_disabled: boolean; is_admin: boolean; subscriber_limit: number; monthly_email_limit: number }>) =>
    request<{ user: AdminUser }>(`/admin/users/${id}`, { method: "PUT", body: data }),

  disableUser: (id: string) => request<{ disabled: boolean }>(`/admin/users/${id}/disable`, { method: "POST" }),
  enableUser: (id: string) => request<{ enabled: boolean }>(`/admin/users/${id}/enable`, { method: "POST" }),

  analytics: () => request<{ users: any; subscribers: any; campaigns: any; emails: any; mrr: number }>("/admin/analytics"),

  userCampaigns: (userId: string) => request<{ campaigns: Campaign[] }>(`/admin/users/${userId}/campaigns`),
  userSubscribers: (userId: string) => request<{ subscribers: Subscriber[] }>(`/admin/users/${userId}/subscribers`),

  resetUsage: (userId: string) => request<{ reset: boolean }>(`/admin/users/${userId}/reset-usage`, { method: "POST" }),
  usage: () => request<{ users: AdminUser[] }>("/admin/usage"),

  auditLog: (params?: { action?: string; page?: number; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.action) qs.set("action", params.action);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.limit) qs.set("limit", String(params.limit));
    return request<{ logs: AuditLogEntry[]; total: number }>(`/admin/audit-log?${qs}`);
  },

  // Vertical prompts
  verticals: {
    list: () => request<{ verticals: any[] }>("/admin/verticals"),
    get: (id: string) => request<{ vertical: any }>(`/admin/verticals/${id}`),
    create: (data: any) => request<{ vertical: any }>("/admin/verticals", { method: "POST", body: data }),
    update: (id: string, data: any) => request<{ vertical: any }>(`/admin/verticals/${id}`, { method: "PUT", body: data }),
    delete: (id: string) => request<{ deleted: boolean }>(`/admin/verticals/${id}`, { method: "DELETE" }),
    test: (id: string, data: { sample_email: string; sample_subject?: string; sender_name?: string; sender_email?: string }) =>
      request<any>(`/admin/verticals/${id}/test`, { method: "POST", body: data }),
  },

  prompts: {
    list: () => request<{ prompts: any[] }>("/admin/prompts"),
    getDefault: (key: string) => request<{ prompt_key: string; system_prompt: string }>(`/admin/prompts/${key}/default`),
    update: (key: string, system_prompt: string) =>
      request<{ ok: boolean }>(`/admin/prompts/${key}`, { method: "PUT", body: { system_prompt } }),
    reset: (key: string) =>
      request<{ ok: boolean; system_prompt: string }>(`/admin/prompts/${key}/reset`, { method: "POST" }),
  },

  // Seed testing
  seed: {
    list: () => request<{ accounts: any[] }>("/admin/seed"),
    create: (data: any) => request<{ account: any }>("/admin/seed", { method: "POST", body: data }),
    update: (id: string, data: any) => request<{ account: any }>(`/admin/seed/${id}`, { method: "PUT", body: data }),
    delete: (id: string) => request<{ deleted: boolean }>(`/admin/seed/${id}`, { method: "DELETE" }),
    test: (id: string) => request<{ ok: boolean; error?: string }>(`/admin/seed/${id}/test`, { method: "POST" }),
    run: () => request<{ message: string }>("/admin/seed/run", { method: "POST" }),
    results: (days?: number) => request<{ results: any[] }>(`/admin/seed/results?days=${days || 7}`),
    summary: (days?: number) => request<{ summary: any }>(`/admin/seed/summary?days=${days || 30}`),
    providers: () => request<{ providers: any[] }>("/admin/seed/providers"),
  },
};

// ── Mail Management (Admin) ──
export interface MailDomain  { id: string; domain: string; is_active: number; }
export interface MailAccount { id: string; email: string; domain: string; display_name?: string; status: string; created_at: string; }
export interface MailAlias   { id: string; source_email: string; destination_email: string; domain: string; }
export interface DnsRecords  { domain: string; serverIp: string; mx: string; spf: string; dmarc: string; dkim: string; }

export interface BatchMailboxInput {
  email: string;
  password: string;
  display_name?: string;
}

export interface BatchMailboxResult {
  email: string;
  success: boolean;
  message: string;
  alreadyExisted: boolean;
}

export interface BatchMailboxResponse {
  summary: { total: number; created: number; failed: number; skipped: number; parseErrors?: number };
  results: BatchMailboxResult[];
  parseErrors?: string[];
}

export const adminMail = {
  domains: () => request<{ domains: MailDomain[]; liveDomains: string[] }>("/admin/mail/domains"),
  addDomain: (domain: string) =>
    request<{ domain: MailDomain; dns: DnsRecords; message: string }>("/admin/mail/domains", { method: "POST", body: { domain } }),
  removeDomain: (id: string) => request<{ deleted: boolean }>(`/admin/mail/domains/${id}`, { method: "DELETE" }),
  domainDns: (id: string) => request<{ dns: DnsRecords }>(`/admin/mail/domains/${id}/dns`),
  accounts: (domain?: string) => request<{ accounts: MailAccount[]; liveCount: number }>(`/admin/mail/accounts${domain ? `?domain=${domain}` : ""}`),
  createAccount: (data: { email: string; password: string; display_name?: string }) =>
    request<{ account: MailAccount; message: string }>("/admin/mail/accounts", { method: "POST", body: data }),
  deleteAccount: (email: string) => request<{ deleted: boolean }>(`/admin/mail/accounts/${encodeURIComponent(email)}`, { method: "DELETE" }),
  changeMailboxPassword: (email: string, password: string) =>
    request<{ updated: boolean }>(`/admin/mail/accounts/${encodeURIComponent(email)}/password`, { method: "PUT", body: { password } }),

  /** Batch create mailboxes from a JS array (max 100) */
  createBatch: (accounts: BatchMailboxInput[]) =>
    request<BatchMailboxResponse>("/admin/mail/accounts/batch", { method: "POST", body: { accounts } }),

  /** Batch create mailboxes by uploading a CSV string.
   *  CSV format: email,password[,display_name]  — header row optional.
   */
  createBatchCsv: async (csvText: string): Promise<BatchMailboxResponse> => {
    const token = localStorage.getItem("moremail_token");
    const res = await fetch(`${API_BASE}/admin/mail/accounts/batch/csv`, {
      method: "POST",
      headers: {
        "Content-Type": "text/csv",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: csvText,
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new ApiError(data.error || "CSV batch request failed", res.status, data);
    }
    return res.json();
  },
  aliases: () => request<{ aliases: MailAlias[] }>("/admin/mail/aliases"),
  createAlias: (source: string, destination: string) =>
    request<{ alias: MailAlias; message: string }>("/admin/mail/aliases", { method: "POST", body: { source, destination } }),
  deleteAlias: (id: string) => request<{ deleted: boolean }>(`/admin/mail/aliases/${id}`, { method: "DELETE" }),
  status: () => request<{ services: Array<{ name: string; running: boolean }> }>("/admin/mail/status"),
  dns: () => request<{ info: string }>("/admin/mail/dns"),
};

// ── Network ──
export interface NetworkOffer {
  id: string;
  user_id: string;
  title: string;
  description?: string;
  product_url?: string;
  commission_pct: number;
  platform_fee_pct: number;
  industry?: string;
  min_audience: number;
  status: "draft" | "active" | "paused" | "closed";
  created_at: string;
  merchant_name?: string;
  merchant_company?: string;
  partner_count?: number;
  total_commissions?: number;
}

export interface NetworkPartnership {
  id: string;
  offer_id: string;
  offer_title: string;
  commission_pct: number;
  product_url?: string;
  merchant_name: string;
  tracking_slug: string;
  status: string;
  earned: number;
  conversion_count: number;
  created_at: string;
}

export const network = {
  marketplace:  (params?: { industry?: string; maxResults?: number; nextToken?: string }) => {
    const qs = new URLSearchParams();
    if (params?.industry)   qs.set("industry", params.industry);
    if (params?.maxResults) qs.set("maxResults", String(params.maxResults));
    if (params?.nextToken)  qs.set("nextToken", params.nextToken);
    return request<{ offers: NetworkOffer[]; nextToken: string | null; count: number }>(`/network/marketplace?${qs}`);
  },
  myOffers:     () => request<{ offers: NetworkOffer[] }>("/network/offers/mine"),
  createOffer:  (data: Partial<NetworkOffer>) =>
    request<{ offer: NetworkOffer }>("/network/offers", { method: "POST", body: data }),
  updateOffer:  (id: string, data: Partial<NetworkOffer>) =>
    request<{ offer: NetworkOffer }>(`/network/offers/${id}`, { method: "PUT", body: data }),
  deleteOffer:  (id: string) =>
    request<{ deleted: boolean }>(`/network/offers/${id}`, { method: "DELETE" }),
  partnerOffer: (id: string) =>
    request<{ partnership: NetworkPartnership }>(`/network/offers/${id}/partner`, { method: "POST" }),
  joinOffer:    (id: string) =>
    request<{ partnership: NetworkPartnership }>(`/network/offers/${id}/partner`, { method: "POST" }),
  partnerships: () => request<{ partnerships: NetworkPartnership[] }>("/network/partnerships"),
  earnings:     () => request<{ totals: any; monthly: any[] }>("/network/earnings"),
};

// ── Seed ──
export const seed = () =>
  request<{ seeded: boolean; credentials: { email: string; password: string } }>("/seed", { method: "POST" });

// ── Public Lead Capture ──
export const captureLead = (email: string, source: string) =>
  request<{ ok: boolean }>("/public/leads", { method: "POST", body: { email, source } });

export { ApiError };

// ── Billing / Plans ──
export interface PricingPlan {
  slug: string;
  name: string;
  monthly_price: number | null;
  annual_price: number | null;
  subscriber_limit: number | null;
  monthly_email_limit: number | null;
  sort_order: number;
  description: string | null;
  cta_label: string | null;
  cta_link: string | null;
  is_popular: number;
  is_active?: number;
  user_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface PlanComparison {
  key: string;
  label: string;
  tooltip: string | null;
  sort_order: number;
  vals: Record<string, boolean | string>;
}

export const billing = {
  plans: () =>
    request<{ plans: PricingPlan[]; comparison: PlanComparison[] }>("/billing/plans"),

  checkout: (plan_slug: string, interval: "month" | "year" = "month") =>
    request<{ url: string; sessionId: string }>("/billing/checkout", {
      method: "POST", body: { plan_slug, interval },
    }),

  portal: () =>
    request<{ url: string }>("/billing/portal", { method: "POST" }),

  subscription: () =>
    request<{ plan: string; stripe_customer_id: string | null; stripe_subscription_id: string | null; current_period_end: string | null; has_billing: boolean }>("/billing/subscription"),
};

// ── Admin Plans ──
export const adminPlans = {
  list: () => request<{ plans: PricingPlan[] }>("/billing/admin/plans"),

  create: (data: Partial<PricingPlan>) =>
    request<{ plan: PricingPlan }>("/billing/admin/plans", { method: "POST", body: data }),

  update: (slug: string, data: Partial<PricingPlan>) =>
    request<{ plan: PricingPlan }>(`/billing/admin/plans/${slug}`, { method: "PUT", body: data }),

  delete: (slug: string) =>
    request<{ deleted: boolean }>(`/billing/admin/plans/${slug}`, { method: "DELETE" }),

  updateFeatures: (slug: string, features: any[]) =>
    request<{ updated: boolean; count: number }>(`/billing/admin/plans/${slug}/features`, {
      method: "PUT", body: { features },
    }),
};

export const apiKeys = {
  list: () =>
    request<{ keys: { id: string; name: string; key_prefix: string; last_used_at: string | null; expires_at: string | null; is_active: number; created_at: string }[] }>("/api-keys"),
  create: (name: string, expires_in_days?: number) =>
    request<{ key: { id: string; name: string; key_prefix: string; raw_key: string; expires_at: string | null; created_at: string }; warning: string }>("/api-keys", {
      method: "POST", body: { name, expires_in_days },
    }),
  revoke: (id: string) =>
    request<{ revoked: boolean }>(`/api-keys/${id}`, { method: "DELETE" }),
};
